import { EventSourcePolyfill } from 'event-source-polyfill';

import { OpenAPI } from 'openapi';

import { ACCESS_TOKEN, HEARTBEAT_TIMEOUT } from 'utils/constants/constants';
import { InvoiceEventType } from 'utils/enums/Invoice';
import {
  getEventData,
  getSSERequestOptions,
  getUrl
} from 'utils/helpers/serverSentEvents';

import { viravaDefault } from 'config/authConfig';

interface CreateSSEConnectionParams {
  token: string;
  userId?: number;
  onEvent: (data: string) => void;
  onRefresh: () => Promise<void>;
  onTokenUpdated?: (newToken: string) => void;
}

export const createSSEConnection = ({
  token,
  userId,
  onEvent,
  onRefresh,
  onTokenUpdated
}: CreateSSEConnectionParams): (() => void) => {
  if (!token || !userId) {
    return () => {};
  }

  let retryTimeout: NodeJS.Timeout | undefined;
  let heartbeatTimeout: NodeJS.Timeout | undefined;
  let retryCount = 0;

  const createEventSource = (): EventSourcePolyfill => {
    const eventSource = new EventSourcePolyfill(
      getUrl(OpenAPI, getSSERequestOptions(userId)),
      {
        headers: {
          Authorization: `Bearer ${token}` || ''
        }
      }
    );
    eventSource.onmessage = (e) => {
      const { eventType } = getEventData(e.data);

      if (eventType === InvoiceEventType.HEARTBEAT) {
        if (heartbeatTimeout) clearTimeout(heartbeatTimeout);

        heartbeatTimeout = setTimeout(() => {
          eventSource.close();
          createEventSource();
        }, HEARTBEAT_TIMEOUT);
      } else {
        onEvent(e.data);
      }
    };

    eventSource.onerror = async (error) => {
      const updateToken = () => {
        const newToken = localStorage.getItem(ACCESS_TOKEN) || '';
        if (onTokenUpdated) {
          onTokenUpdated(newToken);
        }
        return newToken;
      };

      if ((error as any).status === 401) {
        if (viravaDefault.isRefreshTokenExpired()) {
          eventSource.close();
          await viravaDefault.logout(window.origin);
          return;
        }

        await onRefresh();
        updateToken();
        eventSource.close();
        return;
      }

      updateToken();
      eventSource.close();

      if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
      if (retryTimeout) clearTimeout(retryTimeout);

      const retryDelay = Math.min(10000, 2 ** retryCount * 1000);
      retryCount += 1;

      retryTimeout = setTimeout(() => {
        createEventSource();
      }, retryDelay);
    };

    return eventSource;
  };

  const eventSourceInstance = createEventSource();

  return () => {
    eventSourceInstance.close();
    if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
    if (retryTimeout) clearTimeout(retryTimeout);
  };
};
